import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { DollarSign, Clock, Filter, Search, X, Users, Target, Activity, Pencil, Save, Plus } from 'lucide-react';

// ── Reusable Stat Card ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color, trend }) => (
  <div className={`bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 border-l-4 ${color.replace('bg-', 'border-l-')}`}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1 truncate">{title}</p>
    <h3 className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{value}</h3>
    {trend && <p className="text-[10px] sm:text-xs font-medium text-green-600 mt-1 sm:mt-2 flex items-center gap-1 truncate">{trend}</p>}
  </div>
);

const METHOD_COLORS = {
  cash: 'bg-green-100 text-green-700',
  jazzcash: 'bg-red-100 text-red-700',
  easypaisa: 'bg-blue-100 text-blue-700',
  bank: 'bg-indigo-100 text-indigo-700',
};

// ── Today Collections Component ──────────────────────────────────────────────
const TodayCollections = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedOfficer, setSelectedOfficer] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  // Filter options from API
  const [officers, setOfficers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ amount_collected: '', payment_method: '', transaction_id: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Add entry state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ amount_collected: '', payment_method: 'cash', transaction_id: '', notes: '', collected_at: '' });
  const [addProofImage, setAddProofImage] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.payment_method) queryParams.set('payment_method', params.payment_method);
      if (params.officer_id) queryParams.set('officer_id', params.officer_id);
      if (params.area_id) queryParams.set('area_id', params.area_id);

      const url = `/admin/dashboard-analytics/?period=today${queryParams.toString() ? '&' + queryParams.toString() : ''}`;
      const res = await api.get(url);
      setData(res.data);

      // Set filter options from first load
      if (res.data.filter_options) {
        setOfficers(res.data.filter_options.officers || []);
        setAreas(res.data.filter_options.areas || []);
        setPaymentMethods(res.data.filter_options.payment_methods || []);
      }
    } catch (error) {
      console.error("Today collections fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when server-side filters change
  useEffect(() => {
    // Detect user role
    api.get('/users/me/').then(res => {
      const u = res.data;
      if (u.is_superuser) setUserRole('admin');
      else if (u.is_manager) setUserRole('manager');
      else setUserRole(u.role || 'user');
    }).catch(() => {});

    fetchData({
      payment_method: selectedMethod,
      officer_id: selectedOfficer,
      area_id: selectedArea,
    });
  }, [selectedMethod, selectedOfficer, selectedArea]);

  const summary = data?.summary || {};
  const byMethod = data?.by_method || {};
  const byOfficer = data?.by_officer || [];
  const expected = data?.expected_today || {};
  const entries = data?.entries || [];

  // Client-side search filter
  const filteredEntries = entries.filter((e) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      e.customer_username?.toLowerCase().includes(term) ||
      e.officer_name?.toLowerCase().includes(term) ||
      e.customer_area?.toLowerCase().includes(term)
    );
  });

  const collectionRate = expected.total_expected > 0
    ? Math.round((summary.total_collected / expected.total_expected) * 100)
    : 0;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedMethod('');
    setSelectedOfficer('');
    setSelectedArea('');
  };

  const hasActiveFilters = searchTerm || selectedMethod || selectedOfficer || selectedArea;

  const canEdit = userRole === 'admin' || userRole === 'manager';

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      amount_collected: entry.amount_collected,
      payment_method: entry.payment_method,
      transaction_id: entry.transaction_id || '',
      notes: entry.notes || '',
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async () => {
    if (!editingEntry) return;
    setSaving(true);
    try {
      await api.patch(`/collections/${editingEntry.id}/`, {
        amount_collected: parseFloat(editForm.amount_collected),
        payment_method: editForm.payment_method,
        transaction_id: editForm.transaction_id || null,
        notes: editForm.notes || '',
      });
      setEditingEntry(null);
      fetchData({
        payment_method: selectedMethod,
        officer_id: selectedOfficer,
        area_id: selectedArea,
      });
    } catch (err) {
      console.error('Failed to update entry:', err);
      alert(err.response?.data?.detail || 'Failed to update entry.');
    } finally {
      setSaving(false);
    }
  };

  // Customer search for add modal
  const searchCustomers = async (query) => {
    setCustomerSearch(query);
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      const res = await api.get(`/officers/search-customers/?username=${encodeURIComponent(query)}`);
      setCustomerResults(res.data || []);
    } catch (err) {
      console.error('Customer search failed:', err);
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.username);
    setCustomerResults([]);
  };

  const openAddModal = () => {
    setAddForm({ amount_collected: '', payment_method: 'cash', transaction_id: '', notes: '', collected_at: '' });
    setAddProofImage(null);
    setCustomerSearch('');
    setSelectedCustomer(null);
    setCustomerResults([]);
    setShowAddModal(true);
  };

  const handleAddChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSave = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer.');
      return;
    }
    if (!addForm.amount_collected || parseFloat(addForm.amount_collected) <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer: selectedCustomer.id,
        amount_collected: parseFloat(addForm.amount_collected),
        payment_method: addForm.payment_method,
        transaction_id: addForm.transaction_id || null,
        notes: addForm.notes || '',
      };
      if (addForm.collected_at && userRole === 'admin') {
        payload.collected_at = new Date(addForm.collected_at).toISOString();
      }
      if (addProofImage) {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== null && v !== undefined) formData.append(k, v);
        });
        formData.append('proof_image', addProofImage);
        await api.post('/collections/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/collections/', payload);
      }
      setShowAddModal(false);
      fetchData({
        payment_method: selectedMethod,
        officer_id: selectedOfficer,
        area_id: selectedArea,
      });
    } catch (err) {
      console.error('Failed to add entry:', err);
      alert(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to add entry.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Stat Cards */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-blue-600 rounded-full"></span> Today's Performance
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <StatCard title="Collected Today" value={`PKR ${summary.total_collected?.toLocaleString() || 0}`} color="bg-emerald-500" trend={`${summary.collection_count || 0} entries`} />
          <StatCard title="Expected Today" value={`PKR ${Math.round(expected.total_expected || 0).toLocaleString()}`} color="bg-amber-500" trend={`${expected.customer_count || 0} customers due`} />
          <StatCard title="Collection Progress" value={`${collectionRate}%`} color={collectionRate >= 80 ? 'bg-green-500' : collectionRate >= 50 ? 'bg-amber-500' : 'bg-red-500'} trend={collectionRate >= 80 ? 'On track' : 'Below target'} />
          <StatCard title="Active Customers" value={summary.active_customers || 0} color="bg-indigo-500" trend={`${summary.today_activations || 0} new today`} />
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-400" />
          <h3 className="text-sm font-bold text-gray-700">Filters</h3>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="ml-auto text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
              <X size={12} /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customer, officer, area..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Payment Method */}
          <select
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Payment Methods</option>
            {paymentMethods.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>

          {/* Officer */}
          <select
            value={selectedOfficer}
            onChange={(e) => setSelectedOfficer(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Officers</option>
            {officers.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>

          {/* Area */}
          <select
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Areas</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        {/* Active filter badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedMethod && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {paymentMethods.find(m => m.value === selectedMethod)?.label || selectedMethod}
                <button onClick={() => setSelectedMethod('')} className="hover:text-blue-900"><X size={10} /></button>
              </span>
            )}
            {selectedOfficer && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                {officers.find(o => String(o.id) === selectedOfficer)?.name || selectedOfficer}
                <button onClick={() => setSelectedOfficer('')} className="hover:text-purple-900"><X size={10} /></button>
              </span>
            )}
            {selectedArea && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded-full">
                {areas.find(a => String(a.id) === selectedArea)?.name || selectedArea}
                <button onClick={() => setSelectedArea('')} className="hover:text-orange-900"><X size={10} /></button>
              </span>
            )}
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-gray-900"><X size={10} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Officer Summary (if no officer filter) */}
      {!selectedOfficer && byOfficer.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-500 rounded-full"></span> Officer Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byOfficer.map((o) => (
              <div key={o.officer_id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-gray-900 text-sm">{o.officer_name}</p>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{o.count} entries</span>
                </div>
                <p className="text-lg font-bold text-emerald-700">PKR {Math.round(o.collected).toLocaleString()}</p>
                {o.expected > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Expected: PKR {Math.round(o.expected).toLocaleString()}</span>
                      <span>{Math.round((o.collected / o.expected) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          (o.collected / o.expected) >= 0.8 ? 'bg-green-500' :
                          (o.collected / o.expected) >= 0.5 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, Math.round((o.collected / o.expected) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collections Table */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
              <Clock size={18} className="text-gray-400" /> Today's Collections
              <span className="text-xs font-normal text-gray-500 ml-1">
                ({filteredEntries.length} {filteredEntries.length !== entries.length ? `of ${entries.length}` : ''} entries)
              </span>
            </h3>
            {canEdit && (
              <button onClick={openAddModal} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                <Plus size={14} /> Add Entry
              </button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
              <tr>
                <th className="p-4">Customer</th>
                <th className="p-4">Area</th>
                <th className="p-4">Officer</th>
                <th className="p-4">Method</th>
                <th className="p-4">Time</th>
                <th className="p-4 text-right">Amount</th>
                {canEdit && <th className="p-4 text-center">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEntries.length === 0 ? (
                <tr><td colSpan={canEdit ? 7 : 6} className="p-8 text-center text-gray-500">
                  {hasActiveFilters ? 'No entries match your filters.' : 'No collections recorded today.'}
                </td></tr>
              ) : (
                filteredEntries.map((c) => (
                  <tr key={c.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-4 font-semibold text-gray-900">{c.customer_username}</td>
                    <td className="p-4 text-sm text-gray-500">{c.customer_area || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">{c.officer_name || 'Admin'}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${METHOD_COLORS[c.payment_method] || 'bg-gray-100 text-gray-700'}`}>
                        {c.payment_method.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-500">{new Date(c.collected_at).toLocaleTimeString()}</td>
                    <td className="p-4 text-right font-bold text-gray-900">PKR {parseFloat(c.amount_collected).toLocaleString()}</td>
                    {canEdit && (
                      <td className="p-4 text-center">
                        <button onClick={() => openEditModal(c)} className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors" title="Edit entry">
                          <Pencil size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden divide-y divide-gray-100">
          {filteredEntries.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              {hasActiveFilters ? 'No entries match your filters.' : 'No collections recorded today.'}
            </div>
          ) : (
            filteredEntries.map((c) => (
              <div key={c.id} className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-gray-900 text-sm">{c.customer_username}</p>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 text-sm">PKR {parseFloat(c.amount_collected).toLocaleString()}</span>
                    {canEdit && (
                      <button onClick={() => openEditModal(c)} className="p-1 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors" title="Edit">
                        <Pencil size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className={`px-2 py-0.5 rounded-full capitalize ${METHOD_COLORS[c.payment_method] || 'bg-gray-100 text-gray-700'}`}>
                    {c.payment_method.replace('_', ' ')}
                  </span>
                  <span>{c.officer_name || 'Admin'}</span>
                  {c.customer_area && <span>· {c.customer_area}</span>}
                  <span className="ml-auto">{new Date(c.collected_at).toLocaleTimeString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingEntry(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Edit Collection Entry</h3>
              <p className="text-xs text-gray-500 mt-1">{editingEntry.customer_username} — {new Date(editingEntry.collected_at).toLocaleDateString()}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Amount Collected (PKR)</label>
                <input
                  type="number"
                  value={editForm.amount_collected}
                  onChange={(e) => handleEditChange('amount_collected', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Method</label>
                <select
                  value={editForm.payment_method}
                  onChange={(e) => handleEditChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="cash">Cash</option>
                  <option value="jazzcash">JazzCash</option>
                  <option value="easypaisa">EasyPaisa</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={editForm.transaction_id}
                  onChange={(e) => handleEditChange('transaction_id', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => handleEditChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setEditingEntry(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save size={14} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Add Collection Entry</h3>
              <p className="text-xs text-gray-500 mt-1">Record a new collection for a customer</p>
            </div>
            <div className="p-5 space-y-4">
              {/* Customer Search */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Customer</label>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => searchCustomers(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Type username to search..."
                />
                {customerResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customerResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => selectCustomer(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between"
                      >
                        <span className="font-semibold text-gray-900">{c.username}</span>
                        <span className="text-xs text-gray-500">{c.area_name || 'N/A'} · {c.package_name || 'N/A'}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <p className="text-xs text-green-600 mt-1">Selected: {selectedCustomer.username} ({selectedCustomer.area_name || 'N/A'})</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Amount Collected (PKR)</label>
                <input
                  type="number"
                  value={addForm.amount_collected}
                  onChange={(e) => handleAddChange('amount_collected', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Method</label>
                <select
                  value={addForm.payment_method}
                  onChange={(e) => handleAddChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="cash">Cash</option>
                  <option value="jazzcash">JazzCash</option>
                  <option value="easypaisa">EasyPaisa</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>

              {/* Transaction ID */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={addForm.transaction_id}
                  onChange={(e) => handleAddChange('transaction_id', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>

              {/* Proof Image — for non-cash payments */}
              {['jazzcash', 'easypaisa', 'bank'].includes(addForm.payment_method) && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Proof</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAddProofImage(e.target.files[0] || null)}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {addProofImage && <p className="text-xs text-green-600 mt-1">{addProofImage.name}</p>}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => handleAddChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optional"
                />
              </div>

              {/* Collected At — admin only */}
              {userRole === 'admin' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Collected At</label>
                  <input
                    type="datetime-local"
                    value={addForm.collected_at}
                    onChange={(e) => handleAddChange('collected_at', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Leave empty to use current date & time</p>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleAddSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Plus size={14} />
                {saving ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayCollections;
